// Copyright (c) Ping. All rights reserved.
//
// In-call Participants panel — pixel-matched to Ping-Desktop Figma
// "call-participants" (4080:13377):
//   • panel #15171C r12, 1px #222429 border, p12, gap12
//   • header "Participants" (700/20·28) + round close button (X #8C929E)
//   • "N people in the call" right-aligned muted line
//   • rows (44h, p[4,8] gap8): avatar 36 r8 · name (700/14) + status glyph
//     (animated speaking bars OR red mic-off) · round ⋮ button → AnchoredPopover
//     menu (View profile / Pin / Mute) — body-portaled so its z-index always
//     wins over the call surface.
//   • footer Search pill "Message thread" (#222429 r-full) → opens the thread
// Mute is a LOCAL mute (silences the speaker for you only). Tailwind-first with
// design tokens; full light-mode mirror.

import { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Pressable, Text, View } from 'react-native';

import { MicOff, Pin, Smile, UserRound, Volume2, VolumeX, X } from 'lucide-react-native';

import { Avatar } from '@ping/ui';

import type { LiveKitParticipantView } from '@calls/livekit/LiveKitCallClient';
import { useCallStore } from '@calls/store/callStore';

import { AnchoredPopover } from './AnchoredPopover';
import { useCallIconColors } from './callTheme';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

const BARS = [0.4, 0.85, 0.6];
const BAR_DUR = [520, 440, 600];

/** Animated speaking equaliser — 3 brand bars, transform-only (GPU cheap). */
function SpeakingBars(): JSX.Element {
  const vals = useRef(BARS.map(() => new Animated.Value(0.5))).current;
  useEffect(() => {
    const anims = vals.map((v, i) =>
      Animated.loop(
        Animated.sequence([
          Animated.timing(v, { toValue: 1, duration: BAR_DUR[i]!, easing: Easing.inOut(Easing.quad), useNativeDriver: false }),
          Animated.timing(v, { toValue: 0.35, duration: BAR_DUR[i]!, easing: Easing.inOut(Easing.quad), useNativeDriver: false }),
        ]),
      ),
    );
    anims.forEach((a) => a.start());
    return () => anims.forEach((a) => a.stop());
  }, [vals]);
  return (
    <View className="h-[16px] w-[16px] flex-row items-center justify-center gap-[2px]">
      {BARS.map((_, i) => (
        <Animated.View
          key={i}
          style={{ width: 2, height: 12, borderRadius: 999, backgroundColor: '#537EFF', transform: [{ scaleY: vals[i]! }] }}
        />
      ))}
    </View>
  );
}

function MenuItem({
  label,
  onPress,
  icon,
}: {
  label: string;
  onPress: () => void;
  icon: JSX.Element;
}): JSX.Element {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      className="flex-row items-center gap-[8px] rounded-[8px] px-[12px] py-[8px] active:bg-black/[0.04] dark:active:bg-white/[0.06]"
    >
      {icon}
      <Text className="text-[14px] text-light-text-primary dark:text-[#F5F6F8]">{label}</Text>
    </Pressable>
  );
}

function ParticipantRow({
  p,
  speaking,
  pinned,
  onTogglePin,
  onViewProfile,
}: {
  p: LiveKitParticipantView;
  speaking: boolean;
  pinned: boolean;
  onTogglePin: (identity: string) => void;
  onViewProfile?: (accountId: string | undefined) => void;
}): JSX.Element {
  const icon = useCallIconColors();
  const muted = useCallStore((s) => !!s.mutedParticipants[p.identity]);
  const [open, setOpen] = useState(false);

  const btnRef = useRef<any>(null);

  return (
    <View className="h-[44px] flex-row items-center gap-[8px] rounded-[8px] px-[8px] py-[4px]">
      <Avatar size="small" shape="rounded" initials={initials(p.name)} />
      <View className="min-w-0 flex-1 flex-row items-center gap-[4px]">
        <Text
          className="shrink text-[14px] font-bold leading-[22px] text-light-text-primary dark:text-[#F5F6F8]"
          numberOfLines={1}
        >
          {p.name}
          {p.isLocal ? ' (You)' : ''}
        </Text>
        {!p.audioEnabled || muted ? (
          <MicOff size={16} strokeWidth={1.5} color="#FF5353" />
        ) : speaking ? (
          <SpeakingBars />
        ) : null}
      </View>
      <Pressable
        ref={btnRef}
        onPress={() => setOpen((v) => !v)}
        accessibilityLabel={`Options for ${p.name}`}
        className="h-[28px] w-[28px] items-center justify-center rounded-full active:opacity-60"
      >
        <Text className="text-[18px] leading-[18px] text-[#8C929E]">⋯</Text>
      </Pressable>
      <AnchoredPopover anchorRef={btnRef} open={open} onClose={() => setOpen(false)} placement="bottom" align="end">
        <View className="w-[200px] gap-[2px] rounded-[12px] border border-light-border-secondary bg-light-surface-primary p-[4px] dark:border-dark-surface-pressed dark:bg-dark-surface-panel">
          <MenuItem
            label="View profile"
            onPress={() => {
              setOpen(false);
              onViewProfile?.(p.accountId);
            }}
            icon={<UserRound size={16} strokeWidth={1.5} color={icon.primary} />}
          />
          <MenuItem
            label={pinned ? 'Unpin' : 'Pin'}
            onPress={() => {
              setOpen(false);
              onTogglePin(p.identity);
            }}
            icon={<Pin size={16} strokeWidth={1.5} color={pinned ? '#537EFF' : icon.primary} />}
          />
          {!p.isLocal ? (
            <MenuItem
              label={muted ? 'Unmute for me' : 'Mute for me'}
              onPress={() => {
                setOpen(false);
                useCallStore.getState().toggleParticipantMute(p.identity);
              }}
              icon={
                muted ? (
                  <Volume2 size={16} strokeWidth={1.5} color={icon.primary} />
                ) : (
                  <VolumeX size={16} strokeWidth={1.5} color="#FF5353" />
                )
              }
            />
          ) : null}
        </View>
      </AnchoredPopover>
    </View>
  );
}

export interface PeoplePanelProps {
  readonly participants: LiveKitParticipantView[];
  readonly speakingSet: Set<string>;
  readonly pinnedIdentity: string | null;
  readonly onTogglePin: (identity: string) => void;
  readonly onClose: () => void;
  readonly onOpenThread?: () => void;
  readonly onViewProfile?: (accountId: string | undefined) => void;
}

export function PeoplePanel({
  participants,
  speakingSet,
  pinnedIdentity,
  onTogglePin,
  onClose,
  onOpenThread,
  onViewProfile,
}: PeoplePanelProps): JSX.Element {
  return (
    <View className="h-full w-[360px] flex-col gap-[12px] overflow-hidden rounded-[12px] border border-light-border-secondary bg-light-surface-panel p-[12px] dark:border-dark-surface-elevated dark:bg-dark-surface-panel">
      {/* Header */}
      <View className="h-[28px] flex-row items-center gap-[12px]">
        <Text className="flex-1 text-[20px] font-bold leading-[28px] text-light-text-primary dark:text-[#F5F6F8]">
          Participants
        </Text>
        <Pressable
          onPress={onClose}
          accessibilityLabel="Close participants"
          hitSlop={8}
          className="h-[28px] w-[28px] items-center justify-center rounded-full active:opacity-60"
        >
          <X size={20} strokeWidth={1.5} color="#8C929E" />
        </Pressable>
      </View>

      {/* Count */}
      <Text className="px-[8px] text-right text-[14px] leading-[20px] text-light-text-muted dark:text-[#6B7280]">
        {participants.length} {participants.length === 1 ? 'person' : 'people'} in the call
      </Text>

      {/* Rows */}
      <View className="min-h-0 flex-1">
        {participants.map((p) => (
          <ParticipantRow
            key={p.identity}
            p={p}
            speaking={speakingSet.has(p.identity) && p.audioEnabled}
            pinned={pinnedIdentity === p.identity}
            onTogglePin={onTogglePin}
            {...(onViewProfile ? { onViewProfile } : {})}
          />
        ))}
      </View>

      {/* Footer — message thread shortcut */}
      <Pressable
        onPress={onOpenThread}
        accessibilityLabel="Open call thread"
        className="h-[40px] flex-row items-center gap-[12px] rounded-full border border-light-border-secondary bg-light-surface-secondary px-[12px] dark:border-dark-surface-pressed dark:bg-dark-surface-elevated"
      >
        <Text className="flex-1 text-[16px] leading-[22px] text-light-text-muted dark:text-[#6B7280]">
          Message thread
        </Text>
        <Smile size={20} strokeWidth={1.5} color="#6B7280" />
      </Pressable>
    </View>
  );
}
